import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { getProducts, resolveProductPrices } from '@/api/product.api';
import { getProductAvailability } from '@/api/ticketSchedule.api';
import { getBundles, resolveBundlePrices, type ResolvedBundlePrice } from '@/api/bundle.api';
import { getPaymentMethods } from '@/api/paymentMethod.api';
import {
  getShiftSummary,
  lookupSales,
  getCurrentShift,
  openShift,
  closeShift,
} from '@/api/sale.api';
import { getUserDevice } from '@/api/device.api';
import type { SearchParams } from '@/types/api';

export function useProducts(params?: SearchParams) {
  return useQuery({
    queryKey: ['products', params ?? null],
    queryFn: () => getProducts(params),
    staleTime: 60_000,
  });
}

export function useAvailability(productId: string | null, date: string, enabled = true) {
  return useQuery({
    queryKey: ['availability', productId, date],
    queryFn: () => getProductAvailability(productId as string, date),
    enabled: Boolean(productId) && enabled,
    staleTime: 15_000, // slot capacity moves
  });
}

export function useBundles(params?: SearchParams) {
  return useQuery({
    queryKey: ['bundles', params ?? null],
    queryFn: () => getBundles(params),
    staleTime: 60_000,
  });
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
    staleTime: 5 * 60_000,
  });
}

export function useShiftSummary(dateISO: string) {
  return useQuery({
    queryKey: ['shiftSummary', dateISO],
    queryFn: () => getShiftSummary(dateISO),
    staleTime: 30_000,
  });
}

/** The cashier's currently-open till session (null when none is open). */
export function useCurrentShift() {
  return useQuery({
    queryKey: ['shift', 'current'],
    queryFn: getCurrentShift,
    staleTime: 15_000,
  });
}

export function useOpenShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (openingFloat: number) => openShift(openingFloat),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift', 'current'] }),
  });
}

export function useCloseShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      countedCash: number;
      denominations?: Record<string, number> | null;
      note?: string | null;
    }) => closeShift(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift', 'current'] });
      qc.invalidateQueries({ queryKey: ['shiftSummary'] });
    },
  });
}

export function useOrderLookup(
  params: { q: string; date: string; page: number; perPage: number; scope?: 'own' | 'all' },
  enabled = true,
) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => lookupSales(params),
    enabled,
    placeholderData: keepPreviousData, // keep the current page visible while the next loads
  });
}

export function useDeviceBinding() {
  return useQuery({
    queryKey: ['deviceBinding'],
    queryFn: getUserDevice,
    staleTime: 5 * 60_000,
  });
}

/**
 * PricingSchedule-aware prices for the visit date, keyed by product id. The
 * catalog base price is a weekday default; the real charge depends on the date
 * (weekend / holiday / school-holiday rules), so we resolve it here to keep the
 * displayed price in sync with what the sale will charge. `ids` is sorted into
 * the query key so the cache is stable regardless of input order.
 */
export function useResolvedProductPrices(ids: string[], date: string) {
  const key = [...ids].sort();
  return useQuery({
    queryKey: ['resolvedProductPrices', date, key],
    queryFn: async () => {
      const res = await resolveProductPrices(
        key.map((productId) => ({ productId })),
        date,
      );
      const out: Record<string, number> = {};
      for (const [id, p] of Object.entries(res.prices ?? {})) {
        if (typeof p?.price === 'number') out[id] = p.price;
      }
      return out;
    },
    enabled: key.length > 0 && Boolean(date),
    staleTime: 60_000,
  });
}

/** PricingSchedule-aware bundle prices for the visit date, keyed by bundle id. */
export function useResolvedBundlePrices(ids: string[], date: string) {
  const key = [...ids].sort();
  return useQuery<Record<string, ResolvedBundlePrice>>({
    queryKey: ['resolvedBundlePrices', date, key],
    queryFn: () => resolveBundlePrices(key, date),
    enabled: key.length > 0 && Boolean(date),
    staleTime: 60_000,
  });
}
